import React, { PropsWithChildren, useContext, useEffect, useMemo, useReducer, useState } from "react";

type ScrollState = {
  scrollTarget: HTMLElement | Window,
  throttleMs: number,
  posX: number,
  posY: number,  
  vX: number,
  vY: number
};

const defaults: ScrollState = {
  scrollTarget: window,
  throttleMs: 200,
  posX: 0,
  posY: 0,
  vX: 0,
  vY: 0
}

export const ScrollContext = React.createContext<ScrollState>(defaults);

export function ScrollProvider({ children, scrollTarget, throttleMs }: PropsWithChildren & Pick<ScrollState, "scrollTarget" | "throttleMs">) {  
  const [posX, setPosX] = useState<number>(defaults.posX);
  const [posY, setPosY] = useState<number>(defaults.posY);
  const [vX, setVX] = useState<number>(defaults.vX);
  const [vY, setVY] = useState<number>(defaults.vY);
  
  useEffect(() => {
    let currPos : [number, number];
    const timeline : { pos: [number, number], t: number }[] = [];
    let velocityInterval : NodeJS.Timeout | undefined;
    let debounceTimeout : NodeJS.Timeout | undefined;
    function onScroll() {
      currPos = scrollTarget instanceof Window 
        ? [scrollTarget.scrollX, scrollTarget.scrollY]
        : [scrollTarget.scrollLeft, scrollTarget.scrollTop];

      // add current position to timeline
      timeline.unshift({ pos: currPos, t: Date.now() });
      timeline.length = Math.min(timeline.length, 2);

      if (!velocityInterval) {
        velocityInterval = setInterval(
          () => {
            if (timeline.length < 2) {
              setVX(0);
              setVY(0);
            }
            else {
              const final = timeline[0];
              const initial = timeline[timeline.length-1];
              setVX((final.pos[0] - initial.pos[0])/(Date.now() - initial.t) * 1000);
              setVY((final.pos[1] - initial.pos[1])/(Date.now() - initial.t) * 1000);
            }      

            // remove an item from the timeline
            // stop interval when none are left
            timeline.shift();
            if (!timeline.length) {
              clearInterval(velocityInterval);
              velocityInterval = undefined;
            }
          },
          20
        );
      }
      
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
        debounceTimeout = undefined;
      }
      
      // throttled to update position
      debounceTimeout = setTimeout(
        () => {
          setPosX(currPos[0]);
          setPosY(currPos[1]);
        },
        throttleMs
      );
    }

    onScroll();
    scrollTarget.addEventListener("scroll", onScroll);
    return () => {
      scrollTarget.removeEventListener("scroll", onScroll);
      clearInterval(velocityInterval);
    }
  }, [scrollTarget, throttleMs]);

  const stateValue: ScrollState = {
    scrollTarget,
    throttleMs,
    posX, posY,
    vX, vY
  };

  return <ScrollContext.Provider value={stateValue}>
    {children}
  </ScrollContext.Provider>;
}

export function useScroll() : ScrollState {
  const scroll = useContext(ScrollContext);
  return scroll;
}

/**
 * Subscribe to scroll direction
 * @param params.threshold - minimum scroll speed needed to trigger direction change (px/sec)
 * @returns 
 */
export function useVerticalScrollBehavior(params: { threshold: number }) {
  const scrollState = useScroll();
  const [direction, setDirection] = useState<"up" | "down" | null>(null);
  const [isScrolling, setIsScrolling] = useState<boolean>(false);

  useEffect(() => {
    if (direction !== "up" && scrollState.vY < -params.threshold) {
      setDirection("up")
    }
    else if (direction !== "down" && scrollState.vY > params.threshold) {
      setDirection("down");
    }
    setIsScrolling(Math.abs(scrollState.vY) > params.threshold);
  }, [direction, scrollState.vY]);

  return { scrollDirection: direction, isScrolling, posY: scrollState.posY, vY: scrollState.vY };
}